import { apiFetch } from './client';

// Stable wire string from core.assistant_suggestion.origin. The SPA
// switches its detail-view labelling on this — a 'rule' suggestion shows
// "from your monthly invoice routine", a 'check' shows "the system noticed".
export type SuggestionOrigin = 'check' | 'rule';

// Mirrors the status CHECK constraint. Soft-deleted rows aren't surfaced
// (the "Sluta bevaka" verb closes them) so the SPA only ever sees these two.
export type SuggestionStatus = 'open' | 'approved';

// How the Assistant page should render the Approve affordance for a
// suggestion. Driven by the underlying Check.Action() declaration on the
// backend.
//   - 'intent'   → Approve materialises a CommandIntent (none today).
//   - 'navigate' → Renders "Take me there"; SPA routes to action_navigate.
//   - 'guidance' → No button; the summary text IS the instruction.
export type ActionKind = 'intent' | 'navigate' | 'guidance';

// 'destructive' triggers the extra-confirmation rule + red primary on the
// approve button per the design system.
export type SuggestionTone = 'default' | 'destructive';

// Server-pre-rendered blast-radius payload. Detail values are per-source;
// the SPA renders defensively (unknown fields are ignored).
export interface BlastRadius {
  source_label: string;
  affected_teams?: string[];
  row_count: number;
  reversible_until?: string;
  tone: SuggestionTone;
  detail?: unknown;
}

export interface Suggestion {
  id: string;
  dedup_key: string;
  origin: SuggestionOrigin;
  check_key?: string;
  rule_id?: string;
  title: string;
  summary: string;
  blast_radius: BlastRadius;
  status: SuggestionStatus;
  command_intent_id?: string;
  created_at: string;
  action_kind: ActionKind;
  // Relative SPA path the "Take me there" button routes to. Only set when
  // action_kind === 'navigate'. The SPA prefixes /orgs/<slug> via React
  // Router's relative-link resolution.
  action_navigate?: string;
  // has_details is true when the suggestion's underlying check implements
  // DetailedCheck on the server — the SPA shows the "View details" item in
  // the 3-dot menu only when this is true.
  has_details: boolean;
  // has_fix is true when a per-row fix materializer is registered for this
  // check. Detail rows render either way; the picker controls only show
  // when has_fix is true.
  has_fix: boolean;
  // Category for the translated badge + the Settings filter. Owned by the
  // SERVER (internal/services/automation/categories.go) and enforced there by
  // test, because the SPA's own copy of this map went stale and rendered a raw
  // "other" for every check added after it was written. Absent only for a
  // rule-origin suggestion or an unknown check key — render no badge then.
  category?: string;
}

interface ListSuggestionsResponse {
  suggestions: Suggestion[];
}

export async function listOpenSuggestions(
  token: string | null,
  slug: string,
): Promise<Suggestion[]> {
  const res = await apiFetch<ListSuggestionsResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/assistant/suggestions`,
    { token },
  );
  return res.suggestions;
}

export async function getSuggestion(
  token: string | null,
  slug: string,
  id: string,
): Promise<Suggestion> {
  return apiFetch<Suggestion>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/assistant/suggestions/${id}`,
    { token },
  );
}

// stopWatchingSuggestion is the "Sluta bevaka" verb — soft-deletes the
// suggestion AND disables the check that raised it in one server-side tx.
// Re-arming the check is a Settings → Inställningar action.
export async function stopWatchingSuggestion(
  token: string | null,
  slug: string,
  id: string,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/assistant/suggestions/${id}/stop-watching`,
    { token, method: 'POST' },
  );
}

// Approve materialises a core.command_intent server-side and returns
// 204. Phase 1 returns 501 — callers should render "not yet available"
// when that comes back. The client layer maps the 501 to a typed error
// so call sites don't sniff for HTTP codes.
export class ApproveNotImplementedError extends Error {
  constructor() {
    super('Approve not implemented in Phase 1');
    this.name = 'ApproveNotImplementedError';
  }
}

export async function approveSuggestion(
  token: string | null,
  slug: string,
  id: string,
): Promise<void> {
  try {
    await apiFetch<void>(
      `/api/v1/organizations/${encodeURIComponent(slug)}/assistant/suggestions/${id}/approve`,
      { token, method: 'POST' },
    );
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    if (msg.includes('approve_not_implemented')) {
      throw new ApproveNotImplementedError();
    }
    throw e;
  }
}

// CheckSetting mirrors the per-org override row for one condition check.
// has_materializer tells the SPA whether approve will actually do something
// for this check today — when false, the Settings tab still lets you
// toggle / threshold it but a badge calls out "review-only" so admins
// understand the surface.
export interface CheckSetting {
  key: string;
  title: string;
  enabled: boolean;
  threshold?: number;
  has_materializer: boolean;
  action_kind: ActionKind;
  action_navigate?: string;
  // See Suggestion.category — server-owned.
  category?: string;
}

interface ListChecksResponse {
  checks: CheckSetting[];
}

export async function listCheckSettings(
  token: string | null,
  slug: string,
): Promise<CheckSetting[]> {
  const res = await apiFetch<ListChecksResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/assistant/checks`,
    { token },
  );
  return res.checks;
}

export interface UpsertCheckInput {
  enabled: boolean;
  // Omit (or pass null) to clear the per-org threshold and fall back to
  // the check's default. Pass 0+ to set.
  threshold?: number | null;
}

export async function upsertCheckSetting(
  token: string | null,
  slug: string,
  key: string,
  input: UpsertCheckInput,
): Promise<void> {
  await apiFetch<void>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/assistant/checks/${encodeURIComponent(key)}`,
    { token, method: 'PUT', body: input },
  );
}

// Manual sweep trigger — runs every enabled check for the caller's org
// right now and returns a per-org tally. Same code path as the nightly
// cron: respects the silent flag, fans out a bell if findings land.
export interface SweepResult {
  checks_run: number;
  findings_raised: number;
  findings_skipped: number;
  // Open suggestions the sweep closed because the underlying condition is no
  // longer detected (e.g. the user fixed all the leader-less teams since the
  // last sweep). Surfaced in the post-sweep toast so the closing feels visible.
  findings_auto_resolved: number;
  errors: number;
  elapsed_ms: number;
}

export async function triggerSweep(
  token: string | null,
  slug: string,
): Promise<SweepResult> {
  return apiFetch<SweepResult>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/assistant/sweep`,
    { token, method: 'POST' },
  );
}

// SubjectKind mirrors internal/services/automation/detail.go.
export type SubjectKind = 'team' | 'member' | 'activity' | 'invoice';

// FixActionKind mirrors the server's FixActionKind enum. New entries should
// land on both ends in the same PR.
export type FixActionKind =
  | 'assign_leader'
  | 'merge'
  | 'dismiss'
  | 'navigate'
  | 'edit'
  // record_score renders two labelled score inputs inline; the row's `extra`
  // carries is_home plus the home/away side labels.
  | 'record_score';

// One affected entity on a suggestion's detail view. Per-row widget choice
// is keyed off fix_action_kind; Title / Subtitle are server-rendered.
export interface DetailRow {
  subject_id: string;
  subject_kind: SubjectKind;
  title: string;
  subtitle?: string;
  why?: string;
  fix_action_kind: FixActionKind;
  // Relative SPA path the "Open" link routes to. Empty when no per-subject
  // page exists.
  navigate?: string;
  // Per-kind supplemental payload. Shape is per-check; the SPA renderer
  // for that check knows the schema.
  extra?: unknown;
}

export interface SuggestionDetail {
  suggestion: Suggestion;
  check_key: string;
  // has_fix duplicates the value on suggestion.has_fix for convenience —
  // detail-page renderers branch off this without re-reading the parent.
  has_fix: boolean;
  rows: DetailRow[];
}

export async function getSuggestionDetail(
  token: string | null,
  slug: string,
  id: string,
): Promise<SuggestionDetail> {
  return apiFetch<SuggestionDetail>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/assistant/suggestions/${id}/detail`,
    { token },
  );
}

export interface FixSubjectInput {
  subject_id: string;
  args: Record<string, unknown>;
}

export interface FixSubjectResponse {
  intent_id: string;
}

// fixSubject creates a CommandIntent for one detail row's fix. Returns
// the new pending intent's id — the caller pairs this with approveCommand
// from api/commands.ts to execute it atomically with the user's confirm.
export async function fixSubject(
  token: string | null,
  slug: string,
  id: string,
  input: FixSubjectInput,
): Promise<FixSubjectResponse> {
  return apiFetch<FixSubjectResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/assistant/suggestions/${id}/fix`,
    { token, method: 'POST', body: input },
  );
}

export interface ReevaluateResponse {
  // True when the condition is gone and the suggestion has been closed.
  resolved: boolean;
  // How many rows the condition still covers when it isn't fully fixed (fix one
  // of three missing results and this comes back 2).
  remaining_rows: number;
}

// reevaluateSuggestion re-runs the suggestion's check server-side and reconciles
// the stored row. Call it after a per-row fix has been APPROVED — before that the
// mutation hasn't landed and the condition would still look true.
//
// Without this the suggestion sat in the list until the nightly sweep, so a user
// who had just fixed something was told it still needed fixing, and the only way
// out was to press "run the checks now" — the system asking them to do its
// bookkeeping.
export async function reevaluateSuggestion(
  token: string | null,
  slug: string,
  id: string,
): Promise<ReevaluateResponse> {
  return apiFetch<ReevaluateResponse>(
    `/api/v1/organizations/${encodeURIComponent(slug)}/assistant/suggestions/${id}/reevaluate`,
    { token, method: 'POST' },
  );
}
